import type { MaybeRefOrGetter, Ref } from 'vue';
import { computed, ref, toValue, watch } from 'vue';

export interface UseSplitPanelDrawerOptions {
	split: MaybeRefOrGetter<boolean>;
	/** Expanded state beside the content. Never written while the pane is a drawer, so a persisted value survives narrow layouts. */
	visible: Ref<boolean>;
}

/**
 * State of a pane that sits in a `SplitPanel` beside the content when there's room for it,
 * and opens as a drawer over the content otherwise.
 */
export function useSplitPanelDrawer({ split, visible }: UseSplitPanelDrawerOptions) {
	const drawerOpen = ref(false);

	// A drawer left open would otherwise reappear on the next narrow layout
	watch(() => toValue(split), (isSplit) => {
		if (isSplit) drawerOpen.value = false;
	});

	const drawerVisible = computed({
		get: () => !toValue(split) && drawerOpen.value,
		set: (open: boolean) => {
			if (!toValue(split)) drawerOpen.value = open;
		},
	});

	// A disabled split panel still reserves its pane unless it's collapsed
	const splitPanelCollapsed = computed({
		get: () => !toValue(split) || !visible.value,
		set: (collapsed: boolean) => {
			if (toValue(split)) visible.value = !collapsed;
		},
	});

	const open = computed(() => (toValue(split) ? visible.value : drawerVisible.value));

	function toggle(nextOpen = !open.value) {
		if (toValue(split)) {
			visible.value = nextOpen;
		}
		else {
			drawerVisible.value = nextOpen;
		}
	}

	return {
		drawerVisible,
		splitPanelCollapsed,
		open,
		toggle,
	};
}
